import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import * as QRCode from 'qrcode';
import Button from 'src/components/Button';
import Checkbox from 'src/components/Checkbox';
import DateComponent from 'src/components/DateComponent';
import Icon from 'src/components/Icon';
import Input from 'src/components/Input';
import {
    useCreateMfaChallenge,
    useSetupTotp,
    useUpdateEmailMfaEnabled,
    useVerifyMfaChallenge,
} from 'src/queries/useMfa';
import { MfaChallenge, MfaMethodType, MfaStatus, TotpSetupResult } from 'src/api/mfa';
import { parseError } from 'src/utils';
import {
    getVerifiedMfaMethod,
    getVerifiedMfaMethods,
    MFA_METHOD_LABELS,
    sortMfaMethodTypesBySecurity,
} from 'src/utils/mfaMethods';
import styles from './styles.module.scss';

const SUPPORT_EMAIL = 'support@graphiumhealth.com';
const MFA_METHOD_OPTION_UI: { [key in MfaMethodType]: { description: string; icon: string; badge?: string } } = {
    email: {
        description: 'Code sent to your account email',
        icon: 'envelope',
    },
    sms: {
        description: 'Code sent by text message',
        icon: 'mobile-alt',
    },
    totp: {
        badge: 'Most secure',
        description: 'Code from an authenticator app',
        icon: 'shield-alt',
    },
};

interface MfaSetupFlowProps {
    status: MfaStatus;
    methodTypes?: MfaMethodType[];
    purpose?: 'verify' | 'enroll';
    allowRememberDevice?: boolean;
    onVerified?: () => void;
}

function MfaSetupFlow({ status, methodTypes, purpose = 'verify', allowRememberDevice = false, onVerified }: MfaSetupFlowProps) {
    const createChallenge = useCreateMfaChallenge();
    const verifyChallenge = useVerifyMfaChallenge();
    const setupTotp = useSetupTotp();
    const updateEmailMfaEnabled = useUpdateEmailMfaEnabled();
    const availableMethods = useMemo(() => {
        const configuredMethods = (methodTypes || status.policy.allowedMethods || []).filter(
            (method, index, methods) => MFA_METHOD_LABELS[method] && methods.indexOf(method) === index,
        );
        const methods =
            purpose === 'verify'
                ? configuredMethods.filter((method) =>
                      method === 'email'
                          ? status.user.accountEmail?.verifiedInd && status.user.accountEmail?.mfaEnabledInd
                          : getVerifiedMfaMethod(status.user.methods, method),
                  )
                : configuredMethods.filter((method) => method !== 'email' || status.user.accountEmail?.verifiedInd);

        return sortMfaMethodTypesBySecurity(methods);
    }, [methodTypes, purpose, status.policy.allowedMethods, status.user.accountEmail, status.user.methods]);
    const [selectedMethod, setSelectedMethod] = useState<MfaMethodType>(availableMethods[0] || 'email');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [code, setCode] = useState('');
    const [challenge, setChallenge] = useState<MfaChallenge | null>(null);
    const [totpSetupResult, setTotpSetupResult] = useState<TotpSetupResult | null>(null);
    const [totpQrCodeUrl, setTotpQrCodeUrl] = useState('');
    const [totpQrCodeError, setTotpQrCodeError] = useState('');
    const [showTotpManualSetup, setShowTotpManualSetup] = useState(false);
    const [selectedExistingMethodId, setSelectedExistingMethodId] = useState<number | null>(null);
    const [showMethodSelector, setShowMethodSelector] = useState(false);
    const [rememberDevice, setRememberDevice] = useState(false);
    const [error, setError] = useState('');
    const availableMethodKey = availableMethods.join('|');

    useEffect(() => {
        if (availableMethods.length > 0 && !availableMethods.includes(selectedMethod)) {
            setSelectedMethod(availableMethods[0]);
        }
    }, [availableMethods, selectedMethod]);

    useEffect(() => {
        setChallenge(null);
        setTotpSetupResult(null);
        setTotpQrCodeUrl('');
        setTotpQrCodeError('');
        setShowTotpManualSetup(false);
        setCode('');
        setError('');
        setSelectedExistingMethodId(null);
    }, [selectedMethod]);

    useEffect(() => {
        setShowMethodSelector(false);
    }, [availableMethodKey]);

    useEffect(() => {
        if (!allowRememberDevice) {
            setRememberDevice(false);
        }
    }, [allowRememberDevice]);

    const verifiedMethod =
        selectedMethod === 'email' ? status.user.accountEmail : getVerifiedMfaMethod(status.user.methods, selectedMethod);
    const verifiedSmsMethods = getVerifiedMfaMethods(status.user.methods, 'sms');
    const verifiedTotpMethods = getVerifiedMfaMethods(status.user.methods, 'totp');
    const selectedSmsMethodId = selectedExistingMethodId || verifiedSmsMethods[0]?.usrMfaMethodId || null;
    const selectedSmsMethod = verifiedSmsMethods.find((method) => method.usrMfaMethodId === selectedSmsMethodId);
    const isEnrollment = purpose === 'enroll';
    const isSmsSetup = selectedMethod === 'sms' && isEnrollment;
    const isTotpSetup = selectedMethod === 'totp' && isEnrollment;

    async function handleSendChallenge() {
        if (selectedMethod !== 'email' && selectedMethod !== 'sms') {
            return;
        }

        const trimmedPhoneNumber = phoneNumber.trim();
        if (isSmsSetup && !trimmedPhoneNumber) {
            setError('Enter a phone number to receive a text message.');
            return;
        }

        try {
            setError('');
            if (isEnrollment && selectedMethod === 'email' && !status.user.accountEmail?.mfaEnabledInd) {
                await updateEmailMfaEnabled.mutateAsync(true);
            }
            const nextChallenge = await createChallenge.mutateAsync({
                methodType: selectedMethod,
                ...(isSmsSetup ? { phoneNumber: trimmedPhoneNumber } : {}),
                ...(!isSmsSetup && selectedMethod === 'sms' && selectedSmsMethodId
                    ? { methodId: selectedSmsMethodId }
                    : {}),
            });
            setChallenge(nextChallenge);
            setCode('');
        } catch (e) {
            setError(parseError(e));
        }
    }

    async function handleVerifyChallenge() {
        if (!challenge || (selectedMethod !== 'email' && selectedMethod !== 'sms')) {
            return;
        }

        try {
            setError('');
            await verifyChallenge.mutateAsync({
                challengeId: challenge.challengeId,
                methodType: selectedMethod,
                code: code.trim(),
                ...(isSmsSetup ? { phoneNumber: phoneNumber.trim() } : {}),
                ...(allowRememberDevice && rememberDevice
                    ? { rememberDevice: true, deviceType: 'web' as const, deviceLabel: 'This browser' }
                    : {}),
            });
            onVerified?.();
        } catch (e) {
            setError(parseError(e));
        }
    }

    async function handleSetupTotp() {
        try {
            setError('');
            setTotpQrCodeError('');
            const result = await setupTotp.mutateAsync();
            setTotpSetupResult(result);
            setShowTotpManualSetup(false);
            setCode('');
            try {
                setTotpQrCodeUrl(
                    await QRCode.toDataURL(result.otpauthUrl, {
                        errorCorrectionLevel: 'M',
                        margin: 2,
                        scale: 6,
                    }),
                );
            } catch {
                setTotpQrCodeUrl('');
                setTotpQrCodeError('Unable to generate QR code. Use the setup secret instead.');
                setShowTotpManualSetup(true);
            }
        } catch (e) {
            setError(parseError(e));
        }
    }

    async function handleVerifyTotp() {
        try {
            setError('');
            await verifyChallenge.mutateAsync({
                methodType: 'totp',
                code: code.trim(),
                ...(totpSetupResult?.secret ? { totpSecret: totpSetupResult.secret } : {}),
                ...(allowRememberDevice && rememberDevice
                    ? { rememberDevice: true, deviceType: 'web' as const, deviceLabel: 'This browser' }
                    : {}),
            });
            onVerified?.();
        } catch (e) {
            setError(parseError(e));
        }
    }

    if (availableMethods.length === 0) {
        return (
            <div className={styles.error}>
                No MFA method is available for this organization.{' '}
                {purpose === 'verify' ? (
                    <a className={styles.supportLink} href={`mailto:${SUPPORT_EMAIL}`}>
                        Contact support
                    </a>
                ) : null}
            </div>
        );
    }

    function renderMethodTabs() {
        if (availableMethods.length === 1) {
            return null;
        }

        return (
            <div className={styles.methodTabs}>
                {availableMethods.map((method) => {
                    const methodOption = MFA_METHOD_OPTION_UI[method];
                    const isSelected = method === selectedMethod;
                    return (
                        <button
                            aria-pressed={isSelected}
                            key={method}
                            className={classNames(styles.methodTab, { [styles.methodTabActive]: isSelected })}
                            onClick={() => {
                                setSelectedMethod(method);
                                setShowMethodSelector(false);
                            }}
                            type="button"
                        >
                            <span className={styles.methodTabIcon}>
                                <Icon name={methodOption.icon} type="fa" />
                            </span>
                            <span className={styles.methodTabText}>
                                <span className={styles.methodTabLabel}>{MFA_METHOD_LABELS[method]}</span>
                                <span className={styles.methodTabDescription}>{methodOption.description}</span>
                            </span>
                            {methodOption.badge ? (
                                <span className={styles.methodTabBadge}>{methodOption.badge}</span>
                            ) : null}
                        </button>
                    );
                })}
            </div>
        );
    }

    function renderRememberDevice() {
        if (!allowRememberDevice) {
            return null;
        }

        return (
            <label className={styles.rememberDevice}>
                <Checkbox
                    checked={rememberDevice}
                    onChange={(event) => setRememberDevice(event.target.checked)}
                />
                <span>
                    Remember this browser for {status.policy.trustedDeviceTtlHours || 24} hours
                </span>
            </label>
        );
    }

    function renderChallengeMethod() {
        const challengeMethod = selectedMethod === 'sms' ? selectedSmsMethod : verifiedMethod;

        return (
            <div className={styles.methodPanel}>
                {isSmsSetup ? (
                    <Input
                        label="Mobile phone"
                        placeholder="Enter a mobile phone number"
                        value={phoneNumber}
                        onChangeText={setPhoneNumber}
                        disabled={Boolean(challenge)}
                    />
                ) : selectedMethod === 'sms' && verifiedSmsMethods.length > 1 ? (
                    <label className={styles.methodSelectLabel}>
                        Send code to
                        <select
                            className={styles.methodSelect}
                            value={selectedSmsMethodId || ''}
                            onChange={(event) => setSelectedExistingMethodId(parseInt(event.target.value))}
                            disabled={Boolean(challenge)}
                        >
                            {verifiedSmsMethods.map((method) => (
                                <option key={method.usrMfaMethodId} value={method.usrMfaMethodId}>
                                    {method.displayNm || 'Verified text message'}
                                </option>
                            ))}
                        </select>
                    </label>
                ) : (
                    <div className={styles.methodDescription}>
                        {challengeMethod?.displayNm
                            ? `We will send a one-time code to ${challengeMethod.displayNm}.`
                            : `We will send a one-time code by ${MFA_METHOD_LABELS[selectedMethod].toLowerCase()}.`}
                    </div>
                )}

                {!challenge ? (
                    <Button
                        type="primary"
                        text="Send code"
                        onClick={handleSendChallenge}
                        isLoading={createChallenge.isLoading || updateEmailMfaEnabled.isLoading}
                    />
                ) : (
                    <>
                        <div className={styles.sentMessage}>
                            Code sent to {challenge.destination}. Expires{' '}
                            <DateComponent date={challenge.expiresAt} showDateTime={true} useBrowserTimeZone />.
                        </div>
                        <Input
                            label="Verification code"
                            value={code}
                            onChangeText={setCode}
                            autoFocus={true}
                            maxLength={12}
                        />
                        {renderRememberDevice()}
                        <Button
                            type="primary"
                            text="Verify"
                            onClick={handleVerifyChallenge}
                            isLoading={verifyChallenge.isLoading}
                            disabled={!code.trim()}
                        />
                    </>
                )}
            </div>
        );
    }

    function renderTotpMethod() {
        return (
            <div className={styles.methodPanel}>
                {isTotpSetup ? (
                    !totpSetupResult ? (
                        <>
                            <div className={styles.methodDescription}>
                                Open your authenticator app and choose the option to add a new account or profile. Then
                                generate a QR code here and scan it with the app.
                            </div>
                            <Button
                                type="primary"
                                text="Generate QR code"
                                onClick={handleSetupTotp}
                                isLoading={setupTotp.isLoading}
                            />
                        </>
                    ) : (
                        <>
                            <div className={styles.setupStep}>
                                <div className={styles.stepTitle}>1. Scan the QR code</div>
                                <div className={styles.stepDescription}>
                                    First, open your authenticator app and create a new account or profile. Then scan
                                    this QR code.
                                </div>
                                {totpQrCodeUrl ? (
                                    <div className={styles.qrCodeWrap}>
                                        <img
                                            alt="Authenticator app setup QR code"
                                            className={styles.qrCodeImage}
                                            src={totpQrCodeUrl}
                                        />
                                    </div>
                                ) : null}
                                {totpQrCodeError ? <div className={styles.error}>{totpQrCodeError}</div> : null}
                                <div className={styles.secondaryAction}>
                                    <Button
                                        type="transparent"
                                        text={
                                            showTotpManualSetup
                                                ? 'Hide manual setup details'
                                                : 'Show manual setup details'
                                        }
                                        onClick={() => setShowTotpManualSetup(!showTotpManualSetup)}
                                    />
                                </div>
                                {showTotpManualSetup ? (
                                    <div className={styles.manualSetupDetails}>
                                        <div className={styles.methodDescription}>
                                            If scanning does not work, enter this setup secret manually in your
                                            authenticator app.
                                        </div>
                                        <Input label="Setup secret" value={totpSetupResult.secret} readOnly={true} />
                                        <Input
                                            label="Authenticator URL"
                                            value={totpSetupResult.otpauthUrl}
                                            readOnly={true}
                                        />
                                    </div>
                                ) : null}
                            </div>
                            <div className={styles.setupStep}>
                                <div className={styles.stepTitle}>2. Enter the authenticator code</div>
                                <div className={styles.stepDescription}>
                                    Next, enter the code provided by your authenticator app after you scan the QR code.
                                </div>
                                <Input
                                    label="Authenticator code"
                                    value={code}
                                    onChangeText={setCode}
                                    autoFocus={true}
                                    maxLength={12}
                                />
                            </div>
                            {renderRememberDevice()}
                            <Button
                                type="primary"
                                text="Verify authenticator code"
                                onClick={handleVerifyTotp}
                                isLoading={verifyChallenge.isLoading}
                                disabled={!code.trim()}
                            />
                        </>
                    )
                ) : (
                    <>
                        <div className={styles.methodDescription}>
                            {verifiedTotpMethods.length > 1
                                ? 'Enter the code from any enrolled authenticator app.'
                                : `Enter the code from ${verifiedTotpMethods[0]?.displayNm || 'your authenticator app'}.`}
                        </div>
                        <Input
                            label="Authenticator code"
                            value={code}
                            onChangeText={setCode}
                            autoFocus={true}
                            maxLength={12}
                        />
                        {renderRememberDevice()}
                        <Button
                            type="primary"
                            text="Verify"
                            onClick={handleVerifyTotp}
                            isLoading={verifyChallenge.isLoading}
                            disabled={!code.trim()}
                        />
                    </>
                )}
            </div>
        );
    }

    return (
        <>
            {purpose === 'enroll' || showMethodSelector ? renderMethodTabs() : null}
            {selectedMethod === 'totp' ? renderTotpMethod() : renderChallengeMethod()}
            {purpose === 'verify' && availableMethods.length > 1 && !showMethodSelector && !challenge ? (
                <div className={styles.secondaryAction}>
                    <Button type="transparent" text="Try a different method" onClick={() => setShowMethodSelector(true)} />
                </div>
            ) : null}
            {error ? <div className={styles.error}>{error}</div> : null}
        </>
    );
}

export default MfaSetupFlow;
